import React, { useState, useContext, useEffect } from 'react'
import ResponsiveAppBar from '../../components/NavbarCustomer'
import { getList as getOrders } from '../../services/order'
import { UserContext } from '../../context/UserProvider.jsx'
import { DataGrid } from '@mui/x-data-grid'
import { Alert, Box, Button, Chip, Container, TextField, Typography } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import ReplayIcon from '@mui/icons-material/Replay'
import SearchIcon from '@mui/icons-material/Search'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import './orders.css'

const App = () => {
  const navigate = useNavigate()
  const { token } = useContext(UserContext)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 })
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let active = true
    async function load() {
      setLoading(true)
      setError('')
      try {
        const response = await getOrders(token, {
          'user-specific': true,
          'current-page': pagination.page + 1,
          'record-per-page': pagination.pageSize,
          fields: query ? { 'order-number': query } : {},
        })
        if (!active) return
        if (!Array.isArray(response?.collection)) throw new Error(response?.data?.message || 'Unable to load orders. Please try again.')
        setRows(response.collection.map(item => {
          const rawDate = item.raw.updated_last || item.raw.created_last
          const parsedDate = rawDate ? new Date(rawDate.replace(' ', 'T')) : null
          const valid = parsedDate && !isNaN(parsedDate)
          return {
            ...item.data,
            id: item.raw.id,
            status: String(item.data.status || 'Unknown').replace(/\b\w/g, letter => letter.toUpperCase()),
            dateUpdated: valid ? parsedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : item.data.date || '—',
            timeUpdated: valid ? parsedDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '',
          }
        }))
        setTotal(Number(response['data-total']) || 0)
      } catch (caught) {
        if (active) setError(caught.message || 'Unable to load orders. Please try again.')
      } finally {
        if (active) setLoading(false)
      }
    }
    void load()
    return () => { active = false }
  }, [token, pagination.page, pagination.pageSize, query, retry])

  const columns = [
    { field: 'order-number', headerName: 'Order number', flex: 1, minWidth: 250,
      renderCell: ({ row }) => <Link className="orders-number" to={`/orders/${row.id}/information`}>{row['order-number']}</Link> },
    { field: 'dateUpdated', headerName: 'Order date', width: 200,
      renderCell: ({ row }) => <div className="orders-date"><span>{row.dateUpdated}</span><small>{row.timeUpdated}</small></div> },
    { field: 'status', headerName: 'Status', width: 160,
      renderCell: ({ value }) => <Chip size="small" label={value} className={`orders-status ${String(value).toLowerCase() === 'completed' ? 'complete' : 'pending'}`} /> },
    { field: 'actions', headerName: 'Actions', width: 220, sortable: false,
      renderCell: ({ row }) => <div className="orders-actions">
        <Button component={Link} to={`/orders/${row.id}/information`} aria-label={`View ${row['order-number']}`}>View</Button>
        <Button variant="outlined" startIcon={<ReplayIcon />} aria-label={`Reorder ${row['order-number']}`} onClick={() => navigate('/products', { state: { reorderOrderId: row.id } })}>Reorder</Button>
      </div> },
  ]

  function applySearch(value) {
    setQuery(value.trim())
    setPagination(current => ({ ...current, page: 0 }))
  }

  return <>
    <ResponsiveAppBar />
    <Box className="orders-page">
      <Container maxWidth="xl">
        <header className="orders-heading"><Typography component="h1" variant="h4" sx={{ fontWeight: 700, letterSpacing: '-.7px' }}>Orders</Typography><p>Review previous orders, view their details, or reorder your favorites.</p></header>
        <section className="orders-panel" aria-label="Order history">
          <div className="orders-toolbar">
            <div className="orders-section-title"><ReceiptLongOutlinedIcon aria-hidden="true" /><h2>Order history</h2><Chip size="small" label={loading ? 'Loading…' : total} /></div>
            <Box component="form" className="orders-search" onSubmit={event => { event.preventDefault(); applySearch(search) }}>
              <TextField label="Search order number" placeholder="Enter an order number" size="small" value={search} onChange={event => setSearch(event.target.value)} />
              <Button type="submit" variant="contained" disableElevation startIcon={<SearchIcon />}>Search</Button>
              {(search || query) && <Button onClick={() => { setSearch(''); applySearch('') }}>Clear</Button>}
            </Box>
          </div>
          {error && <Alert severity="error" sx={{ mx: 3, mb: 2 }} action={<Button onClick={() => setRetry(value => value + 1)} disabled={loading}>Retry</Button>}>{error}</Alert>}
          <div className="orders-table">
            <DataGrid autoHeight rows={rows} columns={columns} pagination paginationMode="server"
              paginationModel={pagination} onPaginationModelChange={model => setPagination(current => ({ page: model.pageSize !== current.pageSize ? 0 : model.page, pageSize: model.pageSize }))}
              pageSizeOptions={[10, 25, 50, 100]} rowCount={total} loading={loading} rowHeight={76}
              disableColumnFilter disableColumnMenu disableRowSelectionOnClick
              localeText={{ noRowsLabel: query ? 'No orders match this order number' : 'No orders yet' }}
              sx={{ border: 0, minWidth: 830, '& .MuiDataGrid-columnHeaders': { bgcolor: '#f7f9fc', color: '#526176' }, '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 }, '& .MuiDataGrid-cell': { borderColor: '#edf1f5', fontSize: 14 }, '& .MuiDataGrid-row:hover': { bgcolor: '#f8fbff' } }}
            />
          </div>
        </section>
      </Container>
    </Box>
  </>
}
export default App
